import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';
import { cn } from '@/lib/utils';
import { ColumnFilterValue, matchesColumnFilter } from '@/lib/table-query';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  Table as TableInstance,
  getExpandedRowModel,
  ColumnPinningState,
  getGroupedRowModel,
  Column,
  Row,
  Cell,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { DataTablePagination } from '@/components/core';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Button } from '@/components/ui-kit/button';
import { useIsMobile } from '@/hooks/use-media-query';
import { DataCardList } from './data-card-list';

const DEFAULT_COLUMN_PINNING_CONFIG: ColumnPinningState = { left: ['select'], right: [] };

interface AdvanceDataTableProps<TData, TValue> {
  /**
   * Defines the column structure of the table.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/core/column-def)
   */
  columns: ColumnDef<TData, TValue>[];
  /**
   * The dataset to be displayed in the table.
   */
  data: TData[];
  /**
   * Callback function triggered when a row is clicked.
   */
  onRowClick?: (data: TData) => void;
  /**
   * Displays a loading state when `true`.
   */
  isLoading?: boolean;
  /**
   * Displays an error message if provided.
   */
  error?: Error | null;
  /**
   * Function to render a custom column toolbar.
   * @param table - The table instance providing access to table state and methods.
   * @returns A React node to be rendered as part of the toolbar.
   */
  columnsToolbar?: (table: TableInstance<TData>) => React.ReactNode;
  /**
   * Function to render a custom filter toolbar.
   * @param table - The table instance providing access to table state and methods.
   * @returns A React node to be rendered as part of the filter toolbar.
   */
  filterToolbar?: (table: TableInstance<TData>) => React.ReactNode;
  /**
   * Enables expandable row content when `true`.
   */
  isExpandRowContent?: boolean;
  /**
   * Function to render expandable row content.
   * @param rowId - The unique identifier of the row being expanded.
   * @param colSpan - The number of columns spanned by the expanded content.
   * @returns A React node to be rendered as the expanded row content.
   */
  expandRowContent?: (rowId: string, colSpan: number) => React.ReactNode;
  /**
   * Controls pagination state.
   */
  pagination: {
    /**
     * The index of the current page (zero-based).
     */
    pageIndex: number;
    /**
     * The number of rows displayed per page.
     */
    pageSize: number;
    /**
     * The total number of rows available in the dataset.
     */
    totalCount: number;
  };
  /**
   * Callback function for pagination changes.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/modules/pagination)
   * @link [Guide](https://tanstack.com/table/v8/docs/guide/pagination)
   */
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  /**
   * Enables manual pagination when `true`.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/modules/pagination#manualpagination)
   */
  manualPagination?: boolean;
  /**
   * Sorting runs on the server: the table only reports `sorting` and leaves the rows in the order
   * they came. Use with `sorting` / `onSortingChange` (see `useServerTable`).
   */
  manualSorting?: boolean;
  /**
   * Column filters run on the server: the table only reports `columnFilters` and shows every row it
   * got. Use with `columnFilters` / `onColumnFiltersChange` (see `useServerTable`).
   */
  manualFiltering?: boolean;
  /** Controlled sorting; the table keeps its own when left out. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Controlled column filters; the table keeps its own when left out. */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  /**
   * Used to configure which columns should be pinned to either the left or right side of the table.
   * @link [API Docs](https://tanstack.com/table/v8/docs/api/modules/column-pinning)
   */
  columnPinningConfig?: ColumnPinningState;
  /**
   * Extra classes for the `<table>` element, e.g. a `min-w-*` so wide tables scroll horizontally
   * on narrow screens instead of squeezing every column.
   */
  tableClassName?: string;
  /**
   * A stable id per row (e.g. its ItemId). Row selection is keyed by it, so a selection stays on its
   * rows across server-side pages instead of following the row index.
   */
  getRowId?: (row: TData) => string;
  /** Which rows may be selected (others get a disabled checkbox). All rows when left out. */
  canSelectRow?: (row: TData) => boolean;
  /**
   * Clear the selection when the page changes. With server-side paging only the current page's rows
   * are known, so a selection carried across pages couldn't be acted on.
   */
  clearSelectionOnPageChange?: boolean;
  /**
   * How rows show on a phone (below 768px): `'table'` (the default) scrolls sideways, `'cards'`
   * shows each row as a card (see `DataCardList`; a column's `meta.mobile` places it on the card).
   */
  mobileView?: 'table' | 'cards';
  /** Extra classes for a row's card in `'cards'` view (e.g. a highlighted day). */
  getRowClassName?: (row: TData) => string | undefined;
  /** In `'cards'` view, the page's actions on the filter button's line (actions left, filter right). */
  mobileActions?: React.ReactNode;
  /** In `'cards'` view, an element to render the filter button into instead (e.g. in the page header). */
  mobileFilterSlot?: HTMLElement | null;
}

function AdvanceDataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  isLoading = false,
  error = null,
  columnsToolbar,
  filterToolbar,
  isExpandRowContent = true,
  expandRowContent,
  pagination,
  onPaginationChange,
  manualPagination = false,
  manualSorting = false,
  manualFiltering = false,
  sorting: controlledSorting,
  onSortingChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  columnPinningConfig = DEFAULT_COLUMN_PINNING_CONFIG,
  tableClassName,
  getRowId,
  canSelectRow,
  clearSelectionOnPageChange = false,
  mobileView = 'table',
  getRowClassName,
  mobileActions,
  mobileFilterSlot,
}: Readonly<AdvanceDataTableProps<TData, TValue>>) {
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [ownColumnFilters, setOwnColumnFilters] = useState<ColumnFiltersState>([]);
  const [ownSorting, setOwnSorting] = useState<SortingState>([]);
  const columnFilters = controlledColumnFilters ?? ownColumnFilters;
  const sorting = controlledSorting ?? ownSorting;
  const [expanded, setExpanded] = useState({});
  const isMobile = useIsMobile();
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(columnPinningConfig);
  const { t } = useTranslation();

  useEffect(() => {
    setColumnPinning(() => ({
      left: isMobile ? ['select'] : columnPinningConfig.left,
      right: columnPinningConfig.right,
    }));
  }, [isMobile, columnPinningConfig]);

  // `meta.cardOnly` columns are part of the phone card only, never of the table.
  const isCardMode = isMobile && mobileView === 'cards';
  const visibility = useMemo(() => {
    if (isCardMode) return columnVisibility;
    const cardOnly = columns
      .filter((column) => column.meta?.cardOnly && column.id)
      .map((column) => [column.id as string, false] as const);
    return { ...columnVisibility, ...Object.fromEntries(cardOnly) };
  }, [isCardMode, columns, columnVisibility]);

  const table = useReactTable({
    data: error ? [] : data,
    columns,
    state: {
      sorting,
      columnVisibility: visibility,
      rowSelection,
      columnFilters,
      expanded,
      columnPinning,
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    onExpandedChange: setExpanded,
    manualPagination,
    manualSorting,
    manualFiltering,
    defaultColumn: {
      filterFn: (row, columnId, filterValue: ColumnFilterValue) =>
        matchesColumnFilter(row.getValue(columnId), filterValue),
    },
    pageCount: Math.ceil(pagination.totalCount / pagination.pageSize),
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newPagination = updater({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        });
        onPaginationChange?.(newPagination);
      } else {
        onPaginationChange?.(updater);
      }
    },
    enableRowSelection: canSelectRow ? (row) => canSelectRow(row.original) : true,
    ...(getRowId ? { getRowId: (row: TData) => getRowId(row) } : {}),
    enableGrouping: true,
    groupedColumnMode: 'reorder',
    onRowSelectionChange: setRowSelection,
    onSortingChange: onSortingChange ?? setOwnSorting,
    onColumnFiltersChange: onColumnFiltersChange ?? setOwnColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getExpandedRowModel: getExpandedRowModel(),
    onColumnPinningChange: setColumnPinning,
    getGroupedRowModel: getGroupedRowModel(),
  });

  useEffect(() => {
    if (clearSelectionOnPageChange) setRowSelection({});
  }, [clearSelectionOnPageChange, pagination.pageIndex, pagination.pageSize]);

  const renderSkeletonRows = () => {
    return Array.from({ length: pagination.pageSize }).map(() => (
      <TableRow key={`skeleton-${uuidv4()}`}>
        {columns.map(() => (
          <TableCell key={`skeleton-cell-${uuidv4()}`}>
            <Skeleton className="h-4 w-3/4" />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  // The cell spans every column, so on a narrow screen the table scrolls sideways and a centred
  // message would sit half off-screen. Pin it to the left edge at the scroll container's visible
  // width (`cqw`, see the container on <Table> below) so it stays centred in view.
  const renderMessageRow = (message: string, className?: string) => (
    <TableRow>
      <TableCell colSpan={columns.length} className={cn('h-24 p-0 text-center', className)}>
        <div className="sticky left-0 w-[100cqw] break-words px-4 py-2">{message}</div>
      </TableCell>
    </TableRow>
  );

  const renderErrorRow = (error: Error | null) =>
    renderMessageRow(
      error ? `${t('ERROR_LOADING_DATA')} ${error.message}` : t('UNKNOWN_ERROR_OCCURRED'),
      'text-error'
    );

  const renderNoResultFound = () => renderMessageRow(t('NO_RESULTS_FOUND'));

  const renderTableBodyContent = () => {
    if (isLoading) return renderSkeletonRows();
    if (error) return renderErrorRow(error);
    if (!table.getRowModel().rows.length) return renderNoResultFound();

    return table.getRowModel().rows.map((row) => (
      <React.Fragment key={row.id}>
        {renderTableRow(row)}
        {isExpandRowContent && row.getIsExpanded() && expandRowContent?.(row.id, columns.length)}
      </React.Fragment>
    ));
  };

  const renderTableRow = (row: Row<TData>) => (
    <TableRow
      key={row.id}
      data-state={row.getIsSelected() && 'selected'}
      onClick={() => onRowClick?.(row.original)}
      className={row.getIsSelected() ? '!bg-primary-50' : 'cursor-pointer'}
    >
      {row.getVisibleCells().map((cell) => renderTableCell(cell, row))}
    </TableRow>
  );

  const renderTableCell = (cell: Cell<TData, unknown>, row: Row<TData>) => {
    const { column } = cell;
    return (
      <TableCell
        key={cell.id}
        className={cn(
          'py-2.5 pl-4',
          row.getIsSelected() && '!bg-primary-50',
          getCommonPinningClasses(column),
          getFirstColumnClasses(column),
          getControlColumnClasses(column)
        )}
        style={{
          left: column.getIsPinned() === 'left' ? `${column.getStart('left')}px` : undefined,
          right: column.getIsPinned() === 'right' ? `${column.getAfter('right')}px` : undefined,
          width: getColumnWidth(column),
        }}
        onClick={cell.column.id === 'actions' ? (e) => e.stopPropagation() : undefined}
      >
        {cell.column.id === 'select'
          ? renderSelectCell(row)
          : flexRender(cell.column.columnDef.cell, cell.getContext())}
      </TableCell>
    );
  };

  const renderSelectCell = (row: Row<TData>) => (
    <div className="flex items-center gap-2">
      <Checkbox
        checked={row.getIsSelected()}
        disabled={!row.getCanSelect()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        onClick={(e) => e.stopPropagation()}
        aria-label={t('SELECT_ROW')}
        className="border-medium-emphasis data-[state=checked]:border-none border-2"
      />
      {isExpandRowContent && (
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
        >
          {row.getIsExpanded() ? <ChevronUp /> : <ChevronDown />}
        </Button>
      )}
    </div>
  );

  const getCommonPinningClasses = (column: Column<TData, unknown>) => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');

    return clsx(
      isPinned
        ? 'sticky z-[1] opacity-95 bg-white/30 backdrop-blur-md'
        : 'relative z-0 opacity-100',
      isLastLeftPinnedColumn && 'shadow-inset-right',
      isFirstRightPinnedColumn && 'shadow-inset-left'
    );
  };

  // On phones the table is wider than the screen and scrolls sideways, but table auto-layout would
  // still squeeze the first data column to one word per line. Keep it readable.
  const firstDataColumnId = table
    .getVisibleLeafColumns()
    .find((column) => column.id !== 'select')?.id;
  const getFirstColumnClasses = (column: Column<TData, unknown>) =>
    column.id === firstDataColumnId && 'max-sm:min-w-[9rem]';

  // The select and actions columns shrink to their controls (the actions sit at the right edge),
  // leaving the width to the data columns.
  const isActionsColumn = (column: Column<TData, unknown>) => column.id === 'actions';
  const isSelectColumn = (column: Column<TData, unknown>) => column.id === 'select';
  const getColumnWidth = (column: Column<TData, unknown>) =>
    isActionsColumn(column) || isSelectColumn(column) ? undefined : column.getSize();
  const getControlColumnClasses = (column: Column<TData, unknown>) =>
    clsx(
      isActionsColumn(column) && 'w-px whitespace-nowrap pr-4 text-right [&>div]:justify-end',
      isSelectColumn(column) && 'w-px whitespace-nowrap pr-4'
    );

  // No pager under a "no results" or error message; kept while loading so the page doesn't jump, and on
  // a later page that came back empty (e.g. its last row was just removed) so the user can go back.
  const hasNoRows =
    !isLoading &&
    (Boolean(error) ||
      (table.getRowModel().rows.length === 0 && table.getState().pagination.pageIndex === 0));
  const pager = hasNoRows ? null : (
    <DataTablePagination
      showSelectedRowContent={false}
      table={table}
      totalCount={pagination.totalCount}
      onPaginationChange={onPaginationChange}
    />
  );

  if (isMobile && mobileView === 'cards') {
    let message: { text: string; isError?: boolean } | undefined;
    if (error) message = { text: `${t('ERROR_LOADING_DATA')} ${error.message}`, isError: true };
    else if (!table.getRowModel().rows.length) {
      // "No results" when a filter narrowed the list away, "No Data Found" when there is nothing at all.
      const isFiltered = table.getState().columnFilters.length > 0;
      message = { text: t(isFiltered ? 'NO_RESULTS_FOUND' : 'NO_DATA_FOUND') };
    }
    return (
      <div className="flex w-full flex-col gap-5">
        {columnsToolbar ? columnsToolbar(table) : null}
        <DataCardList
          table={table}
          isLoading={isLoading}
          message={message}
          onRowClick={onRowClick}
          getRowClassName={getRowClassName}
          actions={mobileActions}
          filterSlot={mobileFilterSlot}
          filterToolbar={filterToolbar ? filterToolbar(table) : null}
          renderExpanded={
            isExpandRowContent && expandRowContent
              ? (row) => expandRowContent(row.id, 1)
              : undefined
          }
        />
        {pager}
      </div>
    );
  }

  // Nothing to list: just the message, no empty table. While a column filter is on, the header stays,
  // as the filters are in it and the user needs them to widen the search again.
  if (hasNoRows && !error && table.getState().columnFilters.length === 0) {
    return (
      <div className="flex w-full flex-col gap-5">
        {columnsToolbar ? columnsToolbar(table) : null}
        <Card className="w-full rounded-[4px] border-none px-4 py-12 text-center text-sm text-muted-foreground shadow-sm">
          {t('NO_DATA_FOUND')}
        </Card>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {columnsToolbar ? columnsToolbar(table) : null}
      <div className="flex w-full">
        <Card className="w-full border-none rounded-[4px] shadow-sm">
          <CardHeader className="hidden">
            <CardTitle />
            <CardDescription />
          </CardHeader>
          <CardContent className="p-0 md:p-0">
            <Table className={tableClassName} divClassname="[container-type:inline-size]">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="hover:bg-transparent">
                    {headerGroup.headers.map((header) => {
                      const { column } = header;
                      return (
                        <TableHead
                          key={header.id}
                          colSpan={header.colSpan}
                          className={cn(
                            'pr-0',
                            getCommonPinningClasses(column),
                            getFirstColumnClasses(column),
                            getControlColumnClasses(column)
                          )}
                          style={{
                            left:
                              column.getIsPinned() === 'left'
                                ? `${column.getStart('left')}px`
                                : undefined,
                            right:
                              column.getIsPinned() === 'right'
                                ? `${column.getAfter('right')}px`
                                : undefined,
                            width: getColumnWidth(column),
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
                {filterToolbar ? filterToolbar(table) : null}
              </TableHeader>
              <TableBody>{renderTableBodyContent()}</TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      {pager}
    </div>
  );
}
export default AdvanceDataTable;
